<template>
  <div
    v-loading="loading"
    :element-loading-text="$t('COMMON.LOADING')"
    style="min-height: 150px"
  >
    <el-alert
      v-if="!loading && !mapped"
      :title="$t('ESTABLISHMENTS.ISTHERA_NOT_MAPPED')"
      type="warning"
      :closable="false"
      show-icon
    />

    <div v-if="mapped">
      <div class="row mb-4 align-items-center">
        <div class="col-6">
          <span class="display-4">
            {{ $t("ESTABLISHMENTS.ISTHERA_PRODUCTS") }}
          </span>
          <div class="text-muted">
            {{ $t("ESTABLISHMENTS.ISTHERA_DATABASE") }} :
            <code>{{ database }}</code> - {{ products.length }}
          </div>
        </div>
        <div
          class="col-6 text-right"
          v-if="$currentUserCan($permissions.PERM_PROVISION_ISTHERA_PRODUCTS)"
        >
          <base-button
            type="info"
            :disabled="provisioning"
            @click="provision(true)"
          >
            <i class="fas fa-vial mr-1" />
            {{ $t("ESTABLISHMENTS.ISTHERA_SIMULATE") }}
          </base-button>
          <base-button
            type="primary"
            :disabled="provisioning"
            @click="provision(false)"
          >
            <i class="fas fa-cloud-upload-alt mr-1" />
            {{ $t("ESTABLISHMENTS.ISTHERA_PROVISION") }}
          </base-button>
          <base-button
            type="warning"
            :disabled="provisioning"
            @click="deactivateUnlinked()"
          >
            <i class="fas fa-ban mr-1" />
            {{ $t("ESTABLISHMENTS.ISTHERA_PURGE") }}
          </base-button>
        </div>
      </div>

      <el-alert
        v-if="provisioning && progress"
        :title="
          $t('ESTABLISHMENTS.ISTHERA_RUNNING', {
            processed: progress.processed || 0,
            total: progress.total || 0,
          })
        "
        type="info"
        :closable="false"
        show-icon
        class="mb-3"
      />

      <div v-if="lastRun && !provisioning" class="card mb-4">
        <div
          class="card-header d-flex justify-content-between align-items-center py-2"
        >
          <span class="font-weight-bold">
            {{
              lastRun.dry_run
                ? $t("ESTABLISHMENTS.ISTHERA_RESULT_TITLE_DRY")
                : $t("ESTABLISHMENTS.ISTHERA_RESULT_TITLE")
            }}
          </span>
          <span>
            <el-tag v-if="lastRun.dry_run" type="info" size="small">
              {{ $t("ESTABLISHMENTS.ISTHERA_DRY_BADGE") }}
            </el-tag>
            <el-tag
              v-if="lastRun.status === 'FAILED'"
              type="danger"
              size="small"
              class="ml-1"
            >
              {{ $t("ESTABLISHMENTS.ISTHERA_FAILED") }}
            </el-tag>
            <small v-if="lastRun.finished_at" class="text-muted ml-2">
              {{ $t("ESTABLISHMENTS.ISTHERA_FINISHED_AT") }}
              {{ $timeZoneDateFormat(lastRun.finished_at) }}
            </small>
          </span>
        </div>
        <div class="card-body py-3">
          <div class="row text-center">
            <div class="col">
              <div class="h2 mb-0 text-success">
                {{ lastRun.created_count }}
              </div>
              <small class="text-muted">{{
                $t("ESTABLISHMENTS.ISTHERA_CREATED")
              }}</small>
            </div>
            <div class="col">
              <div class="h2 mb-0 text-primary">
                {{ lastRun.updated_count }}
              </div>
              <small class="text-muted">{{
                $t("ESTABLISHMENTS.ISTHERA_UPDATED")
              }}</small>
            </div>
            <div class="col">
              <div class="h2 mb-0 text-muted">
                {{ lastRun.unchanged_count }}
              </div>
              <small class="text-muted">{{
                $t("ESTABLISHMENTS.ISTHERA_UNCHANGED")
              }}</small>
            </div>
            <div class="col">
              <div class="h2 mb-0 text-warning">
                {{ lastRun.deactivated_count }}
              </div>
              <small class="text-muted">{{
                $t("ESTABLISHMENTS.ISTHERA_DEACTIVATED")
              }}</small>
            </div>
            <div v-if="lastRun.purge" class="col">
              <div class="h2 mb-0 text-warning">
                {{ lastRun.purged_count }}
              </div>
              <small class="text-muted">{{
                $t("ESTABLISHMENTS.ISTHERA_PURGED")
              }}</small>
            </div>
            <div class="col">
              <div
                class="h2 mb-0"
                :class="lastRun.error_count ? 'text-danger' : 'text-muted'"
              >
                {{ lastRun.error_count }}
              </div>
              <small class="text-muted">{{
                $t("ESTABLISHMENTS.ISTHERA_ERRORS")
              }}</small>
            </div>
          </div>

          <div v-if="lastRun.failure_message" class="alert alert-danger mt-3 mb-0">
            {{ lastRun.failure_message }}
          </div>

          <div
            v-if="lastRun.errors && lastRun.errors.length"
            class="mt-3"
          >
            <strong>{{ $t("ESTABLISHMENTS.ISTHERA_ERROR_DETAILS") }}</strong>
            <ul class="mb-0 mt-1 small text-danger">
              <li v-for="(e, i) in lastRun.errors" :key="i">
                <code v-if="e.sku || e.product_id">{{
                  e.sku || "#" + e.product_id
                }}</code>
                <code v-else>{{ $t("ESTABLISHMENTS.ISTHERA_ERROR_GLOBAL") }}</code>
                : {{ e.error }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="row mb-3">
        <div class="col-md-4 mb-2">
          <el-input
            v-model="search"
            :placeholder="$t('ESTABLISHMENTS.ISTHERA_SEARCH')"
            clearable
            prefix-icon="el-icon-search"
          />
        </div>
        <div class="col-md-3 mb-2">
          <el-select
            v-model="deptFilter"
            clearable
            filterable
            :placeholder="$t('ESTABLISHMENTS.ISTHERA_ALL_DEPARTEMENTS')"
            class="w-100"
          >
            <el-option
              v-for="d in departements"
              :key="d"
              :label="d"
              :value="d"
            />
          </el-select>
        </div>
        <div class="col-md-3 mb-2">
          <el-select
            v-model="statusFilter"
            clearable
            :placeholder="$t('ESTABLISHMENTS.ISTHERA_ALL_STATUSES')"
            class="w-100"
          >
            <el-option
              v-for="s in [
                'create',
                'link',
                'update',
                'synced',
                'conflict',
                'deactivate',
              ]"
              :key="s"
              :label="actionLabel(s)"
              :value="s"
            />
          </el-select>
        </div>
        <div class="col-md-2 mb-2 text-right text-muted small pt-2">
          {{ filteredProducts.length }} / {{ products.length }}
        </div>
      </div>

      <el-table
        :data="filteredProducts"
        :row-key="rowKey"
        class="table-responsive align-items-center table-flush"
        header-row-class-name="thead-light"
        :empty-text="$t('ESTABLISHMENTS.ISTHERA_NO_RESULT')"
      >
        <el-table-column
          :label="$t('ESTABLISHMENTS.ISTHERA_CODE_ACHAT')"
          prop="code_achat"
          min-width="130"
          sortable
        >
          <template v-slot="{ row }">
            <span v-if="row.code_achat">{{ row.code_achat }}</span>
            <span v-else class="text-muted">-</span>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('ESTABLISHMENTS.ISTHERA_COMMENTAIREA')"
          prop="commentairea"
          min-width="150"
          sortable
        >
          <template v-slot="{ row }">
            <router-link
              v-if="row.commentairea && row.product_id"
              :to="`/inventory/products/view/${row.product_id}`"
            >
              {{ row.commentairea }}
            </router-link>
            <span v-else-if="row.commentairea">{{ row.commentairea }}</span>
            <span v-else class="text-muted">-</span>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('ESTABLISHMENTS.ISTHERA_CODE_BARRE')"
          prop="code_barre"
          min-width="140"
          sortable
        >
          <template v-slot="{ row }">
            <span v-if="row.code_barre">{{ row.code_barre }}</span>
            <span v-else class="text-muted">-</span>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('COMMON.DESCRIPTION')"
          prop="description"
          min-width="200"
          sortable
        />
        <el-table-column
          :label="$t('ESTABLISHMENTS.ISTHERA_DEPARTEMENT')"
          prop="departement"
          min-width="110"
          sortable
        >
          <template v-slot="{ row }">
            {{ row.departement }}
            <div
              v-if="
                row.in_isthera &&
                row.erp_departement &&
                row.erp_departement !== row.departement
              "
              class="small text-muted"
            >
              {{
                $t("ESTABLISHMENTS.ISTHERA_IN_ERP", {
                  value: row.erp_departement,
                })
              }}
            </div>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('COMMON.PRICE')"
          min-width="100"
          align="right"
        >
          <template v-slot="{ row }">
            {{ $formatCurrency(row.prix1) }}
            <div
              v-if="
                row.isthera_prix1 !== null &&
                row.isthera_prix1 !== undefined &&
                row.isthera_prix1 !== row.prix1
              "
              class="small text-muted"
            >
              {{
                $t("ESTABLISHMENTS.ISTHERA_IN_CAISSE", {
                  value: $formatCurrency(row.isthera_prix1),
                })
              }}
            </div>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('ESTABLISHMENTS.ISTHERA_ACTIVE')"
          min-width="100"
          align="center"
        >
          <template v-slot="{ row }">
            <el-tag
              v-if="row.actif !== null"
              :type="row.actif ? 'success' : 'info'"
              size="small"
            >
              {{ row.actif ? $t("COMMON.YES") : $t("COMMON.NO") }}
            </el-tag>
            <span v-else class="text-muted">-</span>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('ESTABLISHMENTS.ISTHERA_STATUS')"
          min-width="160"
          align="center"
        >
          <template v-slot="{ row }">
            <el-tag :type="actionType(row.action)" size="small">
              {{ actionLabel(row.action) }}
            </el-tag>
            <div
              v-if="row.conflict"
              class="small text-muted mt-1"
              style="word-break: normal; line-height: 1.3"
            >
              {{ row.conflict }}
            </div>
          </template>
        </el-table-column>
        <el-table-column
          v-if="$currentUserCan($permissions.PERM_PROVISION_ISTHERA_PRODUCTS)"
          :label="''"
          min-width="140"
          align="center"
        >
          <template v-slot="{ row }">
            <base-button
              v-if="
                row.action === 'deactivate' ||
                (row.action === 'conflict' && !row.product_id)
              "
              type="warning"
              size="sm"
              :disabled="provisioning || syncingRow === rowKey(row)"
              @click="rowDeactivate(row)"
            >
              <i class="fas fa-ban mr-1" />
              {{ $t("ESTABLISHMENTS.ISTHERA_DEACTIVATE_ONE") }}
            </base-button>
            <base-button
              v-else-if="row.product_id && row.action !== 'conflict'"
              type="info"
              size="sm"
              :disabled="provisioning || syncingRow === rowKey(row)"
              @click="rowSync(row)"
            >
              <i class="fas fa-sync-alt mr-1" />
              {{ $t("ESTABLISHMENTS.ISTHERA_SYNC_ONE") }}
            </base-button>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import { Table, TableColumn, Tag, Alert, Input, Select, Option } from "element-ui";
import swal from "sweetalert2";

export default {
  name: "establishment-view-isthera-products",

  components: {
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Tag.name]: Tag,
    [Alert.name]: Alert,
    [Input.name]: Input,
    [Select.name]: Select,
    [Option.name]: Option,
  },

  props: ["establishment"],

  data() {
    return {
      products: [],
      database: null,
      mapped: false,
      loading: false,
      provisioning: false,
      progress: null,
      pollTimer: null,
      search: "",
      deptFilter: "",
      statusFilter: "",
      lastRun: null,
      syncingRow: null,
    };
  },

  computed: {
    departements() {
      return [...new Set(this.products.map((p) => p.departement).filter(Boolean))].sort();
    },

    filteredProducts() {
      const q = (this.search || "").trim().toLowerCase();
      return this.products.filter((p) => {
        if (this.deptFilter && p.departement !== this.deptFilter) {
          return false;
        }
        if (this.statusFilter && p.action !== this.statusFilter) {
          return false;
        }
        if (!q) {
          return true;
        }
        return [
          p.commentairea,
          p.description,
          p.code_barre,
          p.code_achat,
          p.departement,
          p.fournisseur,
        ]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));
      });
    },
  },

  created() {
    this.get();
  },

  beforeDestroy() {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
    }
  },

  methods: {
    async get() {
      if (!this.establishment || !this.establishment.id) {
        return;
      }
      this.loading = true;
      try {
        const response = await this.$store.dispatch(
          "establishments/istheraProducts",
          this.establishment.id
        );
        this.products = response.data || [];
        this.database = response.meta ? response.meta.database : null;
        this.mapped = response.meta ? response.meta.mapped : false;
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      } finally {
        this.loading = false;
      }

      // Show the last provisioning result (if any) when (re)opening the tab.
      if (this.mapped) {
        this.loadLastRun();
      }
    },

    async loadLastRun() {
      try {
        const run = await this.$store.dispatch(
          "establishments/istheraProvisionStatus",
          { id: this.establishment.id }
        );
        if (run && (run.status === "COMPLETED" || run.status === "FAILED")) {
          this.lastRun = run;
        }
      } catch (error) {
        // no last run / ignore
      }
    },

    async provision(dryRun) {
      let purge = false;
      if (!dryRun) {
        // Modal with a checkbox: also deactivate the caisse products absent from the ERP
        // (make the ISTHERA base identical to the ERP).
        const confirmation = await swal.fire({
          title: this.$t("ESTABLISHMENTS.ISTHERA_PROVISION_CONFIRM"),
          input: "checkbox",
          inputValue: 0,
          inputPlaceholder: this.$t("ESTABLISHMENTS.ISTHERA_PROVISION_PURGE_OPTION"),
          type: "question",
          buttonsStyling: false,
          showCancelButton: true,
          confirmButtonText: this.$t("COMMON.YES"),
          cancelButtonText: this.$t("COMMON.NO"),
          confirmButtonClass: "btn btn-primary",
          cancelButtonClass: "btn btn-warning",
        });
        if (!confirmation.isConfirmed) {
          return;
        }
        purge = !!confirmation.value;
      }

      this.provisioning = true;
      this.progress = null;
      try {
        // Async: the endpoint enqueues a job and returns a PENDING run we then poll.
        const run = await this.$store.dispatch(
          "establishments/istheraProvision",
          { id: this.establishment.id, dryRun, purge }
        );
        await this.pollRun(run.id, dryRun);
      } catch (error) {
        this.$showRequestError(error);
        this.provisioning = false;
      }
    },

    rowKey(row) {
      return row.code_achat || (row.product_id ? "erp-" + row.product_id : row.sku);
    },

    actionLabel(action) {
      return {
        create: this.$t("ESTABLISHMENTS.ISTHERA_ACTION_CREATE"),
        link: this.$t("ESTABLISHMENTS.ISTHERA_ACTION_LINK"),
        update: this.$t("ESTABLISHMENTS.ISTHERA_ACTION_UPDATE"),
        synced: this.$t("ESTABLISHMENTS.ISTHERA_ACTION_SYNCED"),
        conflict: this.$t("ESTABLISHMENTS.ISTHERA_ACTION_CONFLICT"),
        deactivate: this.$t("ESTABLISHMENTS.ISTHERA_ACTION_DEACTIVATE"),
      }[action] || action;
    },

    actionType(action) {
      // el-tag only supports '', success, info, warning, danger (no "primary").
      return {
        create: "info",
        link: "",
        update: "warning",
        synced: "success",
        conflict: "danger",
        deactivate: "danger",
      }[action] || "info";
    },

    async rowSync(row) {
      if (!row.product_id) {
        return;
      }
      this.syncingRow = this.rowKey(row);
      try {
        const result = await this.$store.dispatch(
          "establishments/istheraSyncProduct",
          {
            id: this.establishment.id,
            productId: row.product_id,
          }
        );
        // The sync answers 200 even when it refused the product (barcode conflict, missing
        // configuration): show the reason instead of a success.
        const errors = (result && result.errors) || [];
        if (errors.length) {
          this.$notify({
            type: "danger",
            message: errors
              .map((e) => (typeof e === "string" ? e : e.error))
              .join(" "),
          });
        } else {
          this.$notify({
            type: "success",
            message: this.$t("ESTABLISHMENTS.ISTHERA_SYNC_ONE_DONE"),
          });
        }
        await this.get();
      } catch (error) {
        this.$showRequestError(error);
      } finally {
        this.syncingRow = null;
      }
    },

    async rowDeactivate(row) {
      if (!row.code_achat) {
        return;
      }
      const confirmation = await swal.fire({
        title:
          row.action === "conflict"
            ? this.$t("ESTABLISHMENTS.ISTHERA_DEACTIVATE_DUPLICATE_CONFIRM")
            : this.$t("ESTABLISHMENTS.ISTHERA_DEACTIVATE_ONE_CONFIRM"),
        type: "warning",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-danger",
        cancelButtonClass: "btn btn-warning",
      });
      if (!confirmation.value) {
        return;
      }
      this.syncingRow = this.rowKey(row);
      try {
        await this.$store.dispatch("establishments/istheraDeactivateProduct", {
          id: this.establishment.id,
          codeAchat: row.code_achat,
        });
        this.$notify({
          type: "success",
          message: this.$t("ESTABLISHMENTS.ISTHERA_DEACTIVATE_ONE_DONE"),
        });
        await this.get();
      } catch (error) {
        this.$showRequestError(error);
      } finally {
        this.syncingRow = null;
      }
    },

    async deactivateUnlinked() {
      this.provisioning = true;
      try {
        // Preview how many products would be deactivated.
        const preview = await this.$store.dispatch(
          "establishments/istheraDeactivateUnlinked",
          { id: this.establishment.id, dryRun: true }
        );
        const count = preview.candidates || 0;
        if (!count) {
          this.$notify({
            type: "info",
            message: this.$t("ESTABLISHMENTS.ISTHERA_PURGE_NONE"),
          });
          return;
        }

        const confirmation = await swal.fire({
          title: this.$t("ESTABLISHMENTS.ISTHERA_PURGE_CONFIRM", { count }),
          type: "warning",
          buttonsStyling: false,
          showCancelButton: true,
          confirmButtonText: this.$t("COMMON.YES"),
          cancelButtonText: this.$t("COMMON.NO"),
          confirmButtonClass: "btn btn-danger",
          cancelButtonClass: "btn btn-warning",
        });
        if (!confirmation.value) {
          return;
        }

        const res = await this.$store.dispatch(
          "establishments/istheraDeactivateUnlinked",
          { id: this.establishment.id, dryRun: false }
        );
        this.$notify({
          type: "success",
          message: this.$t("ESTABLISHMENTS.ISTHERA_PURGE_DONE", {
            deleted: res.deactivated || 0,
          }),
        });
        await this.get();
      } catch (error) {
        this.$showRequestError(error);
      } finally {
        this.provisioning = false;
      }
    },

    pollRun(runId, dryRun) {
      return new Promise((resolve) => {
        const tick = async () => {
          try {
            const run = await this.$store.dispatch(
              "establishments/istheraProvisionStatus",
              { id: this.establishment.id, runId }
            );
            this.progress = run;

            if (run && (run.status === "COMPLETED" || run.status === "FAILED")) {
              await this.onRunFinished(run, dryRun);
              resolve();

              return;
            }
          } catch (error) {
            // keep polling on transient errors
          }
          this.pollTimer = setTimeout(tick, 2000);
        };
        tick();
      });
    },

    async onRunFinished(run, dryRun) {
      this.provisioning = false;
      this.progress = null;
      this.lastRun = run;

      if (run.status === "FAILED") {
        this.$notify({
          type: "danger",
          message:
            this.$t("ESTABLISHMENTS.ISTHERA_FAILED") +
            (run.failure_message ? " : " + run.failure_message : ""),
        });
        return;
      }

      const message = this.$t("ESTABLISHMENTS.ISTHERA_RESULT", {
        created: run.created_count || 0,
        updated: run.updated_count || 0,
        unchanged: run.unchanged_count || 0,
        deactivated: run.deactivated_count || 0,
        errors: run.error_count || 0,
      });
      this.$notify({
        type: run.error_count ? "warning" : "success",
        message:
          (dryRun
            ? this.$t("ESTABLISHMENTS.ISTHERA_SIMULATION_DONE")
            : this.$t("ESTABLISHMENTS.ISTHERA_PROVISION_DONE")) +
          " : " +
          message,
      });
      if (!dryRun) {
        await this.get();
      }
    },
  },
};
</script>
